import React from 'react';
import { Box, Button, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';

// EV-85: визуальный стиль публичной страницы нетворкинга. Палитра нейтральная (графит + серые),
// чтобы страница подходила любому мероприятию и не спорила с его логотипом; цветными остаются
// только смысловые статусы (контакт — зелёный, входящий запрос — янтарный).

export const ACCENT = '#1f2937';
export const ACCENT_HOVER = '#111827';
export const ACCENT_SOFT = '#f1f3f5';
export const INK = '#111827';
export const MUTED = '#6b7280';
export const ACCENT_TEXT = '#1f2937';
export const BORDER = '#e5e7eb';
export const LINK = '#1d4ed8';

// Приглушённые смысловые цвета — только для пары «принять/отклонить» и статусов
export const SOFT = {
  green: { bg: '#e8f5ee', bgHover: '#d7eee1', fg: '#1e6b43', border: '#cbe7d6' },
  red: { bg: '#fbeeee', bgHover: '#f6dddd', fg: '#a23b3b', border: '#f1d3d3' },
};

export const SoftColorButton = styled(Button, { shouldForwardProp: p => p !== 'tone' })(({ tone }) => ({
  background: SOFT[tone].bg,
  color: SOFT[tone].fg,
  borderRadius: 10,
  padding: '5px 14px',
  fontWeight: 600,
  fontSize: 14,
  textTransform: 'none',
  '&:hover': { background: SOFT[tone].bgHover },
  '&.Mui-disabled': { opacity: 0.6, color: SOFT[tone].fg },
}));

export const PageBackground = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: '#f3f4f6',
  color: INK,
  paddingBottom: 48,
  // шрифт темы для всего, что не Typography (бейджи, кнопки-«таблетки»)
  fontFamily: theme.typography.fontFamily,
  '& button': { fontFamily: theme.typography.fontFamily },
}));

export const Card = styled(Paper)(({ theme }) => ({
  // в grid-колонке карточка не должна раздаваться шире экрана из-за длинного текста
  minWidth: 0,
  borderRadius: 14,
  padding: theme.spacing(2),
  boxShadow: '0 1px 2px rgba(17, 24, 39, 0.05)',
  border: `1px solid ${BORDER}`,
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3),
  },
}));

// Главная кнопка (имя историческое — теперь сплошной графит, без градиента)
export const GradientButton = styled(Button)({
  background: ACCENT,
  color: '#fff',
  borderRadius: 10,
  padding: '8px 18px',
  fontWeight: 600,
  textTransform: 'none',
  fontSize: 15,
  '&.MuiButton-sizeSmall': { padding: '5px 14px', fontSize: 14 },
  boxShadow: 'none',
  '&:hover': { background: ACCENT_HOVER, boxShadow: 'none' },
  '&.Mui-disabled': { background: '#e5e7eb', color: '#9ca3af' },
});

export const SoftButton = styled(Button)({
  borderRadius: 10,
  padding: '9px 18px',
  fontWeight: 600,
  textTransform: 'none',
  fontSize: 15,
  color: ACCENT_TEXT,
  background: '#fff',
  border: `1px solid ${BORDER}`,
  '&:hover': { background: ACCENT_SOFT },
});

export const TextButton = styled(Button)({
  borderRadius: 10,
  textTransform: 'none',
  fontWeight: 500,
  fontSize: 15,
  color: MUTED,
});

// Скругление полей ввода в едином стиле со страницей
export const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    background: '#fff',
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT },
  },
  '& label.Mui-focused': { color: ACCENT },
};

export const Pill = ({ children, color = 'default' }) => {
  const palette = {
    default: { bg: '#f3f4f6', fg: '#4b5563' },
    accent: { bg: '#f3f4f6', fg: '#374151' },
    success: { bg: SOFT.green.bg, fg: SOFT.green.fg },
    warning: { bg: '#fcf3e3', fg: '#8a5a12' },
  }[color];
  return (
    <Box
      component='span'
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: 0.25,
        borderRadius: '999px',
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.4,
        background: palette.bg,
        color: palette.fg,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Box>
  );
};

// Короткое уведомление: на телефоне — над нижним меню, на десктопе — сверху по центру.
export const Toast = ({ toast }) => {
  if (!toast) return null;
  const tone = toast.variant === 'error' ? SOFT.red : SOFT.green;
  return (
    <Box
      role='status'
      sx={{
        position: 'fixed',
        top: { xs: 'auto', sm: 24 },
        bottom: { xs: 'calc(72px + env(safe-area-inset-bottom))', sm: 'auto' },
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1400,
        maxWidth: 'calc(100vw - 32px)',
        px: 2,
        py: 1.1,
        borderRadius: '12px',
        background: tone.bg,
        color: tone.fg,
        border: `1px solid ${tone.border}`,
        boxShadow: '0 6px 20px rgba(17, 24, 39, 0.10)',
        fontSize: 14,
        fontWeight: 600,
        whiteSpace: 'pre-line',
        textAlign: 'center',
      }}
    >
      {toast.text}
    </Box>
  );
};
